import React, { useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { useTranslation } from 'react-i18next';
import { IItemForGetShortInfo, UserRoles, IGetCustomersShort, EntityStatus } from 'types';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { useFetchData } from 'hooks/useFetchData';
import { SHOW_ARCHIVED_CUSTOMERS } from 'appConstants';
import { Rule } from 'antd/lib/form';
import { getCustomersShortInfo } from 'backend/customers';
import { ISelectOption } from 'components/Select/types';
import FormSelect from 'components/Select/FormSelect';
import { DropdownRender } from 'components/Select/DropdownRender';
import { ShowArchived } from 'components/Select/DropdownRender/ShowArchived';
import { ArchivedLabel } from 'components/Select/ArchivedLabel';

interface IProps {
	setIDCustomer: (value: Nullable<string>) => void;
	setFetchedCustomers: (value: IItemForGetShortInfo[]) => void;
	IDSelectedCustomer: Nullable<string>;
}

export const CustomerSelect: React.FC<IProps> = React.memo(
	({ setIDCustomer, setFetchedCustomers, IDSelectedCustomer }) => {
		const { loggedUser } = useContext<IAppContext>(AppContext);
		const hasAccess = useRoleAccess([UserRoles.SUPER_USER, UserRoles.TECHNICAL]);
		const { response, loading, refetch, responseError } = useFetchData<IGetCustomersShort, null>(
			hasAccess ? getCustomersShortInfo : null,
		);
		const [showArchived, setShowArchived] = useState<boolean>(
			localStorage.getItem(SHOW_ARCHIVED_CUSTOMERS) === 'true',
		);
		const { t } = useTranslation('error');

		const validationRules = useMemo<Rule[]>(() => [{ required: true, message: t('selectCustomer') }], [t]);

		const mappedOptions = useMemo<ISelectOption[]>(() => {
			const responseData = response ? response.data.customers : [];
			const loggedUserData = loggedUser.customers || [];
			const items = hasAccess ? responseData : loggedUserData;
			const visibleItems = showArchived
				? items
				: items.filter(
						(customer) => customer.status === EntityStatus.Active || customer.id === IDSelectedCustomer,
				  );

			return visibleItems.map((customer) => ({
				...customer,
				additionalContent: customer.status === EntityStatus.Archived ? <ArchivedLabel /> : undefined,
			}));
		}, [hasAccess, response, loggedUser, showArchived, IDSelectedCustomer]);

		useEffect(() => {
			if (response) {
				setFetchedCustomers(response.data.customers);
			} else if (!hasAccess) {
				const accessibleCustomers = loggedUser.customers || [];
				setFetchedCustomers(accessibleCustomers);
			}
		}, [response, loggedUser, hasAccess]);

		const toggleShowArchived = useCallback(() => {
			localStorage.setItem(SHOW_ARCHIVED_CUSTOMERS, JSON.stringify(!showArchived));
			setShowArchived((prevValue) => !prevValue);
		}, [showArchived]);

		const renderSelectMenu = useCallback(
			(menu: React.ReactElement) => (
				<DropdownRender menu={menu}>
					<ShowArchived checked={showArchived} onChange={toggleShowArchived} />
				</DropdownRender>
			),
			[showArchived, toggleShowArchived],
		);

		return (
			<FormSelect
				testId="CustomerSelect"
				name="IDSelectedCustomer"
				label="customerLabel"
				options={mappedOptions}
				fetchError={responseError}
				loading={loading}
				onRefetch={refetch}
				onChange={setIDCustomer}
				validationRules={validationRules}
				keepSearchWithDropdownRender={true}
				dropdownRender={renderSelectMenu}
			/>
		);
	},
);
